import React, { useEffect } from 'react';
import MCQLayout from './MCQLayout';
import useTTS from './useTTS';

const FONT = '"Nunito", "Baloo 2", sans-serif';

/**
 * Generic MCQ game — dispatches by prompt.kind + options[].kind.
 * Replaces 6 individual components: ListenPickImage, ListenPickWord,
 * ImagePickWord, ViToEnMCQ, DefinitionPickWord, ListenDefPickImage.
 *
 * Expected roundData shape:
 *   {
 *     game_type: "listen_pick_image" | ... ,
 *     ui_kind: "mcq",
 *     prompt: { kind: "audio"|"audio_def"|"image"|"text_vi"|"definition", value, lang?, pos?, fallback_word?, fallback_name_vi? },
 *     options: [{ kind: "text"|"image", value, label, tag_id, correct }],
 *     answer_key: string
 *   }
 */
export default function MCQGame({ roundData, onAnswer, palette: P, disabled }) {
  const prompt = roundData?.prompt || {};
  const options = roundData?.options || [];

  const promptContent = <PromptBlock prompt={prompt} palette={P} />;

  // Image options use custom renderer; text options use default MCQLayout button
  const firstOptKind = options[0]?.kind;
  if (firstOptKind === 'image') {
    const layoutOptions = options.map((o) => ({
      label: o.label || o.tag_id || '',
      correct: !!o.correct,
      image_url: o.value,
      word: o.label,
    }));
    return (
      <MCQLayout
        promptContent={promptContent}
        options={layoutOptions}
        onAnswer={onAnswer}
        palette={P}
        disabled={disabled}
        renderOption={(opt, idx, { showResult, borderColor, shadow }) => (
          <div
            style={{
              borderRadius: 14,
              border: `3px solid ${borderColor}`,
              boxShadow: shadow,
              background: '#fff',
              padding: 8,
              aspectRatio: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            {opt.image_url ? (
              <img
                src={opt.image_url}
                alt={opt.word || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
              />
            ) : (
              <div
                style={{
                  width: '100%', height: '100%', borderRadius: 10,
                  background: `hsl(${idx * 70 + 200}, 55%, 88%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 8,
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 800, color: '#333', fontFamily: FONT, textAlign: 'center' }}>
                  {opt.word || '?'}
                </span>
              </div>
            )}
          </div>
        )}
      />
    );
  }

  // Text options → default MCQLayout button renderer
  const layoutOptions = options.map((o) => ({
    label: o.label || o.value || '',
    correct: !!o.correct,
  }));
  return (
    <MCQLayout
      promptContent={promptContent}
      options={layoutOptions}
      onAnswer={onAnswer}
      palette={P}
      disabled={disabled}
    />
  );
}

/** Dispatches prompt rendering by kind. */
function PromptBlock({ prompt, palette: P }) {
  const kind = prompt?.kind;

  if (kind === 'audio') {
    return <AudioPrompt value={prompt.value} lang={prompt.lang} palette={P} caption="Nghe và chọn đáp án đúng" showWord />;
  }
  if (kind === 'audio_def') {
    return <AudioPrompt value={prompt.value} lang={prompt.lang} palette={P} caption="Nghe nghĩa và chọn đáp án đúng" icon="🔊 📖" multiline />;
  }
  if (kind === 'image') {
    return <ImagePrompt url={prompt.value} fallbackWord={prompt.fallback_word} fallbackVi={prompt.fallback_name_vi} palette={P} />;
  }
  if (kind === 'text_vi') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: P.mutedText, fontFamily: FONT }}>
          Từ này nghĩa là gì?
        </div>
        <div
          style={{
            fontSize: 32, fontWeight: 800, color: P.coral, fontFamily: FONT,
            background: P.sand, padding: '12px 28px', borderRadius: 14,
            border: `2.5px solid ${P.ink}`, boxShadow: `0 4px 0 ${P.ink}`,
          }}
        >
          {prompt.value}
        </div>
      </div>
    );
  }
  if (kind === 'definition') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: P.mutedText, fontFamily: FONT }}>
          Đọc nghĩa và chọn từ đúng
        </div>
        <div
          style={{
            fontSize: 18, fontWeight: 600, color: P.ink, fontFamily: FONT,
            background: '#F0F8FF', padding: '16px 24px', borderRadius: 14,
            border: `2px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`,
            maxWidth: 440, textAlign: 'center', lineHeight: 1.5, fontStyle: 'italic',
          }}
        >
          "{prompt.value}"
          {prompt.pos && (
            <span style={{ fontSize: 12, fontWeight: 700, color: P.mutedText, marginLeft: 8, fontStyle: 'normal' }}>
              ({prompt.pos})
            </span>
          )}
        </div>
      </div>
    );
  }
  return null;
}

function AudioPrompt({ value, lang = 'en-GB', palette: P, caption, icon = '🔊', showWord = false, multiline = false }) {
  const { speak } = useTTS();

  useEffect(() => {
    if (value) setTimeout(() => speak(value, lang), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, lang]);

  const replay = () => value && speak(value, lang);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      {caption && (
        <div style={{ fontSize: 14, fontWeight: 700, color: P.mutedText, fontFamily: FONT }}>
          {caption}
        </div>
      )}
      <button
        onClick={replay}
        style={{
          background: P.sand, border: `2.5px solid ${P.ink}`, borderRadius: 16,
          boxShadow: `0 4px 0 ${P.ink}`, padding: '12px 28px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <span style={{ fontSize: multiline ? 36 : 48 }}>{icon}</span>
        {(showWord || multiline) && value && (
          <span
            style={{
              fontSize: multiline ? 15 : 20,
              fontWeight: multiline ? 600 : 700,
              color: P.ink, fontFamily: FONT,
              maxWidth: multiline ? 280 : undefined,
              textAlign: multiline ? 'left' : undefined,
              fontStyle: multiline ? 'italic' : undefined,
            }}
          >
            {multiline ? `"${value}"` : value}
          </span>
        )}
      </button>
    </div>
  );
}

function ImagePrompt({ url, fallbackWord, fallbackVi, palette: P }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: P.mutedText, fontFamily: FONT }}>
        Hình này là từ gì?
      </div>
      {url ? (
        <img
          src={url}
          alt=""
          style={{
            width: 140, height: 140, objectFit: 'cover', borderRadius: 16,
            border: `3px solid ${P.ink}`, boxShadow: `0 4px 0 ${P.ink}`,
          }}
        />
      ) : (
        <div
          style={{
            width: 180, height: 140, borderRadius: 16,
            background: 'linear-gradient(135deg, #D4E7FF 0%, #FFF3E0 100%)',
            border: `3px solid ${P.ink}`, boxShadow: `0 4px 0 ${P.ink}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 8,
          }}
        >
          <span style={{ fontSize: 28, fontWeight: 800, color: P.ink, fontFamily: FONT }}>
            {fallbackWord || '?'}
          </span>
          {fallbackVi && (
            <span style={{ fontSize: 13, fontWeight: 600, color: P.mutedText, fontFamily: FONT }}>
              {fallbackVi}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
